import { ChevronDown, ChevronUp, Info } from "lucide-react";
import { EventExample, EventType } from "./progressData";
import { useState, useEffect } from "react";
import { cn } from "@/util/cn";
import { Link } from "react-router";
import CreditCardIcon from "@/components/icons/CreditCardIcon";
import VisaLogo from "@/res/logos/visa-logo.png";
import MasterCard from "@/res/logos/mastercard-logo.png";
import AmericanExpress from "@/res/logos/amex-logo.png";
import PayPal from "@/res/logos/PayPal_Logo_2022/PayPal_Logo_Horizontal_Full_Color_RGB.png";
import KlarnaLogo from "@/res/logos/klarna-logo.svg?url";
import AfterpayLogo from "@/res/logos/afterpay-logo.png";
import GooglePay from "@/res/logos/Google-Pay-Mark/google-pay-logo.png";
import ApplePay from "@/res/logos/Apple-Pay-Mark/apple-pay-logo.png";
import PayTo from "@/res/logos/payto-logo.png";

type EventProps = {
  event: EventExample;
  isFirst?: boolean;
  isLast?: boolean;
  globalExpandTrigger?: { action: "expand" | "collapse"; id: number } | null;
};

const ReminderBadge = () => (
  <div className="flex items-center justify-center w-5 h-5 border-[2px] border-brand-orange-500 text-brand-orange-500 rounded-full font-bold text-[11px] bg-[#24262e] shadow-md ml-1">
    !
  </div>
);

type ProfileChangeTuple = [string | undefined, string | undefined];
type ProfileChanges = Record<string, ProfileChangeTuple>;

const fieldLabelMap: Record<string, string> = {
  firstName: "First Name",
  lastName: "Last Name",
  otherNames: "Other Names",
  phone: "Phone",
  email: "Email",
  "address.type": "Address Type",
  "address.line1": "Address Line 1",
  "address.line2": "Address Line 2",
  "address.line3": "Address Line 3",
  "address.line4": "Address Line 4",
  "address.city": "City",
  "address.state": "State",
  "address.postcode": "Postcode",
  "address.country": "Country",
  nickname: "Account Nickname",
  wattCapacity: "Panel Capacity",
  defaultAllocation: "Default Allocation",
  accountName: "Account Name",
  accountNumber: "Account Number",
  bsb: "BSB",
  energyCompanyName: "Energy Company",
  otherName: "Other Name",
  billerCode: "Biller Code",
  customerReferenceNumber: "Customer Reference Number",
  paypalEmail: "PayPal Email",
  status: "Status",
};

function labelForField(fieldName: string): string {
  return fieldLabelMap[fieldName] || fieldName.replace(/\./g, " ");
}

function stripTrackerPrefix(value: string | undefined): string {
  if (!value) {
    return "";
  }

  return value.replace(/^::[^:]+::/, "");
}

function isHeaderLine(message: string): boolean {
  return !message.includes(":") && !message.includes("=") && !message.startsWith("$") && !message.includes("changed from");
}

function parseProfileChanges(rawValue: string | undefined): ProfileChanges | null {
  if (!rawValue) {
    return null;
  }

  try {
    const parsed: unknown = JSON.parse(rawValue);
    if (!parsed || typeof parsed !== "object" || Array.isArray(parsed)) {
      return null;
    }

    const result = Object.entries(parsed as Record<string, unknown>).reduce<ProfileChanges>((accumulator, [key, value]) => {
      if (Array.isArray(value) && value.length >= 2) {
        const fromValue = typeof value[0] === "string" ? value[0] : undefined;
        const toValue = typeof value[1] === "string" ? value[1] : undefined;
        accumulator[key] = [fromValue, toValue];
      }
      return accumulator;
    }, {});

    return Object.keys(result).length > 0 ? result : null;
  } catch {
    return null;
  }
}

type PaymentMethodLogoProps = {
  method?: string;
  className?: string;
};

function PaymentMethodLogo({ method, className }: PaymentMethodLogoProps) {
  const normMethod = method?.toLowerCase().trim() || "";

  let logoSrc = "";
  let altText = "Payment Method";
  let bgClass = "bg-white rounded-lg h-10 w-24 sm:h-12 sm:w-28 flex justify-center items-center shadow-md relative opacity-85 hover:opacity-100 transition-opacity duration-300 ml-auto border border-white/10";

  if (normMethod === "paypal") {
    logoSrc = PayPal;
    altText = "PayPal";
  } else if (normMethod === "visa") {
    logoSrc = VisaLogo;
    altText = "Visa";
  } else if (normMethod === "mastercard" || normMethod === "card" || normMethod === "mint") {
    logoSrc = MasterCard;
    altText = "MasterCard";
  } else if (normMethod === "amex") {
    logoSrc = AmericanExpress;
    altText = "American Express";
  } else if (normMethod === "klarna") {
    logoSrc = KlarnaLogo;
    altText = "Klarna";
  } else if (normMethod === "afterpay" || normMethod === "afterpay_clearpay") {
    logoSrc = AfterpayLogo;
    altText = "Afterpay";
  } else if (normMethod === "google" || normMethod === "google_pay" || normMethod === "googlepay") {
    logoSrc = GooglePay;
    altText = "Google Pay";
  } else if (normMethod === "apple" || normMethod === "apple_pay" || normMethod === "applepay") {
    logoSrc = ApplePay;
    altText = "Apple Pay";
  } else if (normMethod === "payto") {
    logoSrc = PayTo;
    altText = "PayTo";
  } else {
    // Fallback default
    logoSrc = VisaLogo;
    altText = "Card";
  }

  // Exact classes from PaymentMethods.tsx for perfect sizing
  let imgClass = "max-w-full object-contain mx-auto";
  if (normMethod === "paypal") {
    imgClass = "max-w-full p-2 max-h-full";
  } else if (normMethod === "visa") {
    imgClass = "h-4 max-w-full";
  } else if (normMethod === "mastercard" || normMethod === "card" || normMethod === "mint") {
    imgClass = "h-8 max-w-full";
  } else if (normMethod === "amex") {
    imgClass = "h-8 max-w-full";
  } else if (normMethod === "klarna") {
    imgClass = "h-6 max-w-full";
  } else if (normMethod === "afterpay" || normMethod === "afterpay_clearpay") {
    imgClass = "h-10 max-w-full";
  } else if (normMethod === "google" || normMethod === "google_pay" || normMethod === "googlepay") {
    imgClass = "h-6 max-w-full";
  } else if (normMethod === "apple" || normMethod === "apple_pay" || normMethod === "applepay") {
    imgClass = "h-6 max-w-full";
  } else if (normMethod === "payto") {
    imgClass = "max-w-full max-h-full p-2";
  }

  return (
    <div className={cn(bgClass, className)}>
      <img src={logoSrc} alt={altText} className={imgClass} />
    </div>
  );
}

export default function Event({ event, isFirst, isLast, globalExpandTrigger }: EventProps) {
  const [isOpen, setIsOpen] = useState(false);

  useEffect(() => {
    if (globalExpandTrigger) {
      setIsOpen(globalExpandTrigger.action === "expand");
    }
  }, [globalExpandTrigger]);

  const date = new Date(event.timestamp);
  const isQuarter = event.update?.startsWith("Q");

  const isValidDate = !isNaN(date.getTime());
  const formattedDateTime = isValidDate
    ? `${date.getDate()} ${date.toLocaleDateString("en-US", { month: "short" })} ${date.getFullYear()} at ${date.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit", hour12: true })}`
    : "";

  // Extract amount message if it starts with $
  const amountMsg = event.messages?.find(msg => msg.startsWith('$'));
  // Filter out the amount message from standard messages
  const otherMessages = event.messages ? event.messages.filter(msg => msg !== amountMsg) : [];

  // Determine summary vs detailed messages using empty line as separator
  const dividerIndex = otherMessages.indexOf("");
  const summaryMessages = dividerIndex !== -1 ? otherMessages.slice(0, dividerIndex) : otherMessages;
  const detailMessages = dividerIndex !== -1 ? otherMessages.slice(dividerIndex + 1) : [];
  const profileChanges = parseProfileChanges(event.payload?.metrics?.profileChanges);
  const title = stripTrackerPrefix(event.title);

  const hasLongContent = detailMessages.length > 0 || !!event.payload;

  return (
    <div className="grid grid-cols-[80px_1fr] sm:grid-cols-[120px_1fr] gap-x-6 min-h-[140px] group w-full">
      {/* 1. Timeline Column: Interrupted Line + Date */}
      <div className="flex flex-col items-center h-full select-none">
        {/* Top Line Segment */}
        <div className={cn("w-[2px] h-6 flex-shrink-0 bg-brand-blue-200/60", isFirst && "opacity-0")} />

        {/* Date Block */}
        <div className="py-2 text-center my-1 min-h-[64px] flex flex-col items-center justify-center">
          {isQuarter ? (
            <>
              <h2 className="text-xl font-bold text-white/95 leading-none">{event.update}</h2>
              <p className="text-[11px] text-brand-neutral-500 font-medium mt-1 leading-none">2022</p>
            </>
          ) : (
            <>
              <h2 className="text-2xl font-bold text-white/95 leading-none">{date.getDate()}</h2>
              <p className="text-[10px] text-brand-neutral-500 font-medium mt-1 leading-none">
                {date.toLocaleDateString("en-US", { month: "short" })}
              </p>
              <p className="text-[10px] text-brand-neutral-500 font-medium mt-0.5 leading-none">
                {date.getFullYear()}
              </p>
            </>
          )}
        </div>

        {/* Bottom Line Segment */}
        <div className={cn("w-[2px] flex-1 bg-brand-blue-200/60", isLast && "opacity-0")} />
      </div>

      {/* 2. Event Card Body Column */}
      <div className="pt-9 pb-6 pr-4 flex flex-col justify-start relative">
        {/* Header with Title and Warning Badge */}
        <div className="flex items-baseline gap-2 mb-2 flex-wrap">
          <h3 className="text-lg font-semibold text-white/90 leading-tight">
            {title}
          </h3>
          {formattedDateTime && (
            <span className="text-xs text-brand-neutral-500 font-medium">
              {formattedDateTime}
            </span>
          )}
          {event.type === EventType.REMINDERS && <ReminderBadge />}
        </div>

        {/* Chevron & Details Row */}
        <div className="flex items-start gap-3">
          {/* Chevron inside card, to the left of the details */}
          {hasLongContent ? (
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="text-white/50 hover:text-white transition-colors pt-1 flex-shrink-0"
            >
              {isOpen ? <ChevronUp className="size-4" /> : <ChevronDown className="size-4" />}
            </button>
          ) : (
            <div className="w-4 flex-shrink-0" />
          )}

          {/* Details Column */}
          <div className="flex-1 flex flex-col">
            {/* Description and Amount */}
            <div className="flex flex-col space-y-1 mb-3">
              {event.description && (
                <p className="text-sm text-brand-neutral-400 leading-normal">{event.description}</p>
              )}
              {amountMsg ? (
                <p className="text-[22px] font-bold text-brand-orange-500 tracking-tight">{amountMsg}</p>
              ) : (
                event.update && !isQuarter && event.update !== "Updated" && event.update !== "Created" && event.update !== event.description && (
                  <p className="text-[22px] font-bold text-brand-orange-500 tracking-tight">{event.update}</p>
                )
              )}
              {event.manualPaymentRequestUuid && event.manualPaymentRequestState !== "Paid" && (
                <div className="mt-2">
                  <Link to={`/buypanels/manual-payment?requestId=${encodeURIComponent(event.manualPaymentRequestUuid)}&source=tracker`}>
                    <button className="px-4 py-2 bg-brand-orange-500 hover:bg-brand-orange-600 text-black font-semibold rounded transition-colors text-sm">
                      Pay balance
                    </button>
                  </Link>
                </div>
              )}
            </div>

            {/* Dynamic Details Block (Collapsible) */}
            <div className="text-sm text-white/80 space-y-1.5 pr-0 sm:pr-12">
              {/* Always show summary messages */}
              {summaryMessages.map((msg, i) => (
                <p key={i} className="leading-relaxed">{msg}</p>
              ))}

              {/* Show details if open */}
              {detailMessages.length > 0 && (
                <div className={cn("overflow-hidden transition-all duration-300 space-y-1.5", isOpen ? "max-h-[1500px] opacity-100 mt-2" : "max-h-0 opacity-0")}>
                  {detailMessages.map((msg, i) => {
                    if (msg === "") {
                      return <div key={i} className="h-2" />;
                    }
                    const cleanMsg = stripTrackerPrefix(msg);
                    if (isHeaderLine(cleanMsg)) {
                      return <p key={i} className="font-semibold text-white/95 mt-3">{cleanMsg}</p>;
                    }
                    return <p key={i} className="leading-relaxed text-white/70">{cleanMsg}</p>;
                  })}
                </div>
              )}

              {/* Read more / read less custom buttons at the bottom of the card content */}
              {detailMessages.length > 0 && (
                <button
                  onClick={() => setIsOpen(!isOpen)}
                  className="flex items-center gap-1.5 text-xs text-brand-orange-500 hover:text-brand-orange-600 hover:underline transition-colors mt-2 font-medium"
                >
                  {isOpen ? (
                    <>
                      read less
                      <ChevronUp className="size-3" />
                    </>
                  ) : (
                    <>
                      read more
                      <ChevronDown className="size-3" />
                    </>
                  )}
                </button>
              )}

              {/* Standard Collapsible Payload Details for other events */}
              {isOpen && event.uuid !== "88888888-8888-8888-8888-888888888882" && event.uuid !== "88888888-8888-8888-8888-888888888881" && (event.payload || event.title?.startsWith('Payout')) && (
                <div className="bg-white/5 rounded-lg p-4 space-y-3 mt-4 border border-white/10 mr-4">
                  {(event.payload?.note || event.payload?.disbursementInfo || event.payload?.changeState || profileChanges) && (
                    <div className="flex items-center gap-2 text-brand-blue-300">
                      <Info className="size-4" />
                      <span className="font-semibold text-sm uppercase tracking-wider">Details</span>
                    </div>
                  )}

                  {event.payload?.note && (
                    <div className="text-sm">
                      <span className="text-brand-neutral-400">Note: </span>
                      <span>{event.payload.note}</span>
                    </div>
                  )}

                  {event.payload?.disbursementInfo && (
                    <div className="space-y-1 text-sm bg-black/20 p-3 rounded">
                      <p><span className="text-brand-neutral-400">Method:</span> {event.payload.disbursementInfo.disbursementType}</p>
                      {event.payload.disbursementInfo.accountIdentifier && (
                        <p><span className="text-brand-neutral-400">Account:</span> {event.payload.disbursementInfo.accountIdentifier}</p>
                      )}
                      <p><span className="text-brand-neutral-400">Status:</span> {event.payload.disbursementInfo.status}</p>

                      {(event.payload.disbursementInfo.status === 'Failed' || event.payload.disbursementInfo.note) && (
                        <div className="mt-2 text-brand-orange-500 font-medium">
                          {event.payload.disbursementInfo.note && (
                            <p className="text-brand-orange-500/80 mt-1 italic">"{event.payload.disbursementInfo.note}"</p>
                          )}
                        </div>
                      )}
                    </div>
                  )}

                  {profileChanges && (
                    <div className="space-y-3 text-sm">
                      {Object.entries(profileChanges).map(([fieldName, [fromValue, toValue]]) => {
                        let displayFrom = fromValue;
                        let displayTo = toValue;
                        if (fieldName === "defaultAllocation") {
                          if (fromValue !== undefined && fromValue !== null) {
                            displayFrom = fromValue === "true" ? "Yes" : "No";
                          }
                          if (toValue !== undefined && toValue !== null) {
                            displayTo = toValue === "true" ? "Yes" : "No";
                          }
                        }
                        return (
                          <div key={fieldName} className="space-y-1 rounded bg-black/20 p-3">
                            <p><span className="text-brand-neutral-400">Field:</span> {labelForField(fieldName)}</p>
                            <p><span className="text-brand-neutral-400">From:</span> {displayFrom || "(none)"}</p>
                            <p><span className="text-brand-neutral-400">To:</span> {displayTo || "(none)"}</p>
                          </div>
                        );
                      })}
                    </div>
                  )}

                  {event.payload?.changeState && !profileChanges && (
                    <div className="space-y-1 text-sm">
                      <p><span className="text-brand-neutral-400">Change:</span> {event.payload.changeState.changeType?.replaceAll(/([A-Z])/g, " $1").trim()}</p>
                      <p><span className="text-brand-neutral-400">From:</span> {event.payload.changeState.fromValue || '(none)'}</p>
                      <p><span className="text-brand-neutral-400">To:</span> {event.payload.changeState.toValue}</p>
                    </div>
                  )}

                  {event.payload?.deviceContext && (event.payload.deviceContext.browserType || event.payload.deviceContext.ipAddress) && (
                    <div className="space-y-1 text-xs text-brand-neutral-400 pt-2 border-t border-white/10">
                      <p>Device: {event.payload.deviceContext.browserType || 'Unknown'} on {event.payload.deviceContext.osVersion || 'Unknown'}</p>
                      <p>IP: {event.payload.deviceContext.ipAddress || 'Unknown'}</p>
                    </div>
                  )}
                </div>
              )}
            </div>
          </div>
        </div>

        {/* Right Aligned Credit Card (acts as expand toggle) */}
        {event.manualPaymentRequestUuid && event.manualPaymentRequestState !== "Paid" && (
          <div className="relative sm:absolute right-auto sm:right-0 top-auto sm:top-9 mt-4 sm:mt-0 pl-6 sm:pl-0 flex flex-col items-start sm:items-end gap-1">
            <button
              onClick={() => setIsOpen(!isOpen)}
              className="flex flex-col items-start sm:items-end gap-1 group/btn cursor-pointer"
            >
              <CreditCardIcon />
            </button>
          </div>
        )}

        {/* Right Aligned Paid Payment Logo */}
        {((event.manualPaymentRequestUuid && event.manualPaymentRequestState === "Paid") ||
          (!event.manualPaymentRequestUuid && (event.payload?.cardInfo || event.paymentMethod))) && (
            <div className="relative sm:absolute right-auto sm:right-0 top-auto sm:top-9 mt-4 sm:mt-0 pl-6 sm:pl-0 flex flex-col items-start sm:items-end gap-1 select-none pointer-events-none">
              <PaymentMethodLogo
                method={event.payload?.cardInfo?.cardType || event.paymentMethod}
                className="ml-0 sm:ml-auto"
              />
            </div>
          )}
      </div>
    </div>
  );
}
